import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { convertToUtc, parseParams } from 'helper/utility'
import { axiosGet, axiosPatch, axiosPost } from 'Redux/axiosHelper'
import {
  AttendeeStatusParams,
  AttendeeStatusResp,
  CheckINOutAttendee,
  CheckinPayload,
  CheckinPayloadType,
  CheckoutPayload,
  CheckoutPayloadType,
  CreateRollCallPayload,
  DirectCheckInOutResp,
  EventAttendanceExportResp,
  EventAttendeesListingResp,
  ExportEventTicketsResp,
  ImportRegistrationsPayload,
  ImportRegistrationsResp,
  GetEventAttendeesParams,
  RollCallResp,
} from 'types/event/eventAttendance'
import { PaginatedResp, Person } from 'types/globalTypes'

// new testing code
const createMutationSuccessHandler =
  (queryClient: any) => (data: DirectCheckInOutResp) => {
    queryClient.setQueryData(
      ['event-participants-status-list'],
      (oldData: AttendeeStatusResp | undefined) =>
        updateAttendeeData(oldData, data),
    )
  }

const getAttendeeType = (
  data: DirectCheckInOutResp,
): 'kid' | 'authorizedAdult' | 'guardian' | 'dropInAttendee' | undefined => {
  if (data.guardianId) return 'guardian'
  if (data.authorizedAdultId) return 'authorizedAdult'
  if (data.kidId) return 'kid'
  if (data.dropInAttendeeId) return 'dropInAttendee'
  return undefined
}

const updateAttendeeData = (
  oldData: AttendeeStatusResp | undefined,
  data: DirectCheckInOutResp,
) => {
  const type = getAttendeeType(data)
  if (!oldData) {
    return {
      kids: type === 'kid' ? [data] : [],
      authorizedAdults: type === 'authorizedAdult' ? [data] : [],
      guardians: type === 'guardian' ? [data] : [],
      dropinAttendees: type === 'dropInAttendee' ? [data] : [],
    }
  }

  const updateAttendeeList = (list: any[], id: string | undefined) =>
    list.map(item =>
      item._id === id
        ? {
            ...item,
            checkInStatus: data.checkInStatus,
            checkOutStatus: data.checkOutStatus,
            checkedInAt: data.checkedInAt,
            checkedOutAt: data.checkedOutAt,
            dropInAttendeeId: data.dropInAttendeeId,
            checkInId: data._id,
          }
        : item,
    )

  return {
    kids:
      type === 'kid'
        ? updateAttendeeList(oldData.kids, data.kidId)
        : oldData.kids,
    authorizedAdults:
      type === 'authorizedAdult'
        ? updateAttendeeList(oldData.authorizedAdults, data.authorizedAdultId)
        : oldData.authorizedAdults,
    guardians:
      type === 'guardian'
        ? updateAttendeeList(oldData.guardians, data.guardianId)
        : oldData.guardians,
    dropinAttendees:
      type === 'dropInAttendee'
        ? updateAttendeeList(oldData.dropinAttendees, data.dropInAttendeeId)
        : oldData.dropinAttendees,
  }
}

//new event code

const getEventAttendee = async (
  eventId: string,
  params: GetEventAttendeesParams,
) => {
  const response = await axiosGet(`v1/attendance/${eventId}`, params)
  return response.data
}

export const useGetEventAttendees = (
  eventId: string,
  params: GetEventAttendeesParams,
) => {
  const eventAttendees = useQuery<PaginatedResp<EventAttendeesListingResp>>(
    ['event-attendees-list'],
    () => getEventAttendee(eventId, params),
    { enabled: false },
  )
  return eventAttendees
}

//export data for attendance table
const getExportEventAttendees = async (eventId: string) => {
  const response = await axiosGet(`v1/attendance/export-data/${eventId}`)
  return response.data
}

export const useGetEventAttendeesExport = (eventId: string) => {
  const eventAttendees = useQuery<EventAttendanceExportResp[]>(
    ['event-attendees-export-list'],
    () => getExportEventAttendees(eventId),
    { enabled: false },
  )
  return eventAttendees
}

//get participants status
const getCheckParticipantStatus = async (params: AttendeeStatusParams) => {
  const parsedParams = parseParams(params)
  const response = await axiosGet(
    `v1/check/participants-status?${parsedParams}`,
  )
  return response.data
}

export const useGetCheckParticipantsStatus = (params: AttendeeStatusParams) => {
  const eventParticipantstatus = useQuery<AttendeeStatusResp>(
    ['event-participants-status-list'],
    () => getCheckParticipantStatus(params),
    { enabled: false },
  )
  return eventParticipantstatus
}

// post request to check in
const checkInParticipantDirectly = async (
  payalod: CheckinPayload | CheckinPayloadType,
) => {
  const response = await axiosPost(
    payalod,
    `v2/check/admin/check-in`,
    '',
    false,
  )
  return response.data
}

export const useCheckInParticipantDirectly = () => {
  const queryClient = useQueryClient()
  return useMutation(checkInParticipantDirectly, {
    onSuccess: createMutationSuccessHandler(queryClient),
  })
}

// post request direct checkout
export const checkOutParticipantDirectly = async (
  payload: CheckoutPayloadType,
) => {
  const response = await axiosPatch(
    payload,
    `v2/check/admin/check-out`,
    '',
    false,
  )
  return response.data
}

export const useCheckoutParticipantDirectly = () => {
  const queryClient = useQueryClient()
  return useMutation(checkOutParticipantDirectly, {
    onSuccess: createMutationSuccessHandler(queryClient),
  })
}

// undo direct checkin checkout
const undoCheckoutParticipantDirectly = async (checkInId: string) => {
  const response = await axiosPatch(
    {},
    `v2/check/admin/check-undo/${checkInId}`,
    '',
    false,
  )
  return response.data
}

export const useUndoCheckoutParticipantDirectly = () => {
  const queryClient = useQueryClient()
  return useMutation(undoCheckoutParticipantDirectly, {
    onSuccess: createMutationSuccessHandler(queryClient),
  })
}

// accept check-in request
const acceptCheckInRequest = async (payload: CheckoutPayloadType) => {
  const response = await axiosPatch(
    payload,
    'v2/check/admin/event-check-in-approval',
  )
  return response.data
}

export const useAcceptCheckInRequest = () => {
  const queryClient = useQueryClient()
  return useMutation(acceptCheckInRequest, {
    onSuccess: createMutationSuccessHandler(queryClient),
  })
}

// checkout from event
const checkoutEventRequest = async (payload: CheckoutPayload) => {
  const response = await axiosPatch(
    payload,
    `v2/check/admin/event-check-out-request`,
  )
  return response.data
}

export const useCheckoutEventRequest = () => {
  const queryClient = useQueryClient()
  return useMutation(checkoutEventRequest, {
    onSuccess: createMutationSuccessHandler(queryClient),
  })
}

// get history of event participant
const getEventParticipantHistory = async (
  memberId: string,
  params: {
    date: string
    eventId: string
  },
) => {
  const response = await axiosGet(
    // `v1/check/recent-check-in/member/${memberId}`,
    `v2/check/admin/check-in-history/${memberId}`,
    params,
  )
  return response.data
}
export const useGetEventParticipantHistory = (
  memberId: string,
  params: {
    date: string
    eventId: string
  },
) => {
  const eventParticipantHistory = useQuery<DirectCheckInOutResp[]>(
    ['event-participant-history'],
    () => getEventParticipantHistory(memberId, params),
    { enabled: false },
  )
  return eventParticipantHistory
}

// approve check-in request
const approveCheckInRequest = async (payload: { checkInId: string }) => {
  const response = await axiosPatch(
    payload,
    `v2/check/admin/event-check-in-approval`,
    'approved successfully',
  )
  return response.data
}

export const useApproveCheckInRequest = () => {
  return useMutation(approveCheckInRequest)
}

// get participant list for drop-call
const getDropCallParticipants = async (
  eventId: string,
  params: { date: string },
) => {
  const response = await axiosGet(
    `v2/check/admin/list/roll-call/${eventId}`,
    params,
  )
  return response.data
}

export const useGetDropCallParticipants = (
  eventId: string,
  params: { date: string },
) => {
  const dropCallParticipants = useQuery<Person[]>(
    ['event-drop-call-participants'],
    () => getDropCallParticipants(eventId, params),
    { enabled: false },
  )
  return dropCallParticipants
}

// create Roll call
const createRollCall = async (payload: CreateRollCallPayload) => {
  const response = await axiosPost(payload, `v1/roll-call/create`)
  return response.data
}

export const useCreateRollCall = () => {
  const queryClient = useQueryClient()
  return useMutation(createRollCall, {})
}

//update roll-call
const updateRollCall = async ({
  payload,
  rollCallId,
}: {
  payload: CreateRollCallPayload
  rollCallId: string
}) => {
  const response = await axiosPatch(payload, `v1/roll-call/${rollCallId}`)
  return response.data
}

export const useUpdateRollCall = () => {
  return useMutation(updateRollCall, {})
}

//get roll-call
const getRollCallForEvent = async (params: {
  date: string | Date
  eventId: string
}) => {
  const response = await axiosGet(`v1/roll-call/history`, params)
  return response.data
}

export const useGetRollCallForEvent = (params: {
  date: string | Date
  eventId: string
}) => {
  const rollCallForEvent = useQuery<RollCallResp[]>(
    ['event-roll-call-history'],
    () => getRollCallForEvent(params),
    { enabled: false },
  )
  return rollCallForEvent
}

// update check-in / check-out time
const updateCheckInCheckOutTime = async ({
  checkInId,
  payload,
}: {
  checkInId: string
  payload: { checkedInAt?: string; checkedOutAt?: string }
}) => {
  const response = await axiosPatch(payload, `v1/check/${checkInId}`, '', false)
  return response.data
}

export const useUpdateCheckInCheckOutTime = () => {
  const queryClient = useQueryClient()
  return useMutation(updateCheckInCheckOutTime, {
    onSuccess: data => {
      queryClient.setQueryData(
        ['event-participant-history'],
        (oldData: DirectCheckInOutResp[] | undefined) => {
          if (!oldData) return [data]
          return oldData.map(item => (item._id === data._id ? data : item))
        },
      )
    },
  })
}

// export ticket IDs + days for an event (reference data for import CSV)
const getExportEventTickets = async (eventId: string) => {
  const response = await axiosGet(`v1/attendance/export-tickets/${eventId}`)
  return response.data
}

export const useGetExportEventTickets = (eventId: string) => {
  return useQuery<ExportEventTicketsResp>(
    ['event-export-tickets', eventId],
    () => getExportEventTickets(eventId),
    { enabled: false },
  )
}


const calculateDropInPrice = async (checkInId: string) => {
  const response = await axiosGet(
    `v1/dropin/calculate-dropIn-price/${checkInId}`,
  )
  return response.data
}

export const useCalcluateDropInPrice = (checkInId: string) => {
  const checkInPrice = useQuery<any>(
    ['event-drop-in-price'],
    () => calculateDropInPrice(checkInId),
    { enabled: false },
  )
  return checkInPrice
}

const importRegistrations = async (payload: ImportRegistrationsPayload) => {
  const response = await axiosPost(payload, 'v1/attendance/import-registrations')
  return response.data as ImportRegistrationsResp
}

export const useImportRegistrations = () => {
  return useMutation(importRegistrations)
}
